import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Button,
  useDisclosure,
} from "@heroui/react";
import DeleteParagraphConfirmModal from "../paragraph/DeleteParagraphConfirmModal";
import EditParagraphMetaModal from "../paragraph/EditParagraphModal";
import SplitMergeModal from "../SplitMergeModal";
import classNames from "classnames";
import TextareaAutosize from "react-textarea-autosize";
import { useDispatch, useSelector } from "react-redux";
import { editParagraphAsync, regenerateParagraphContentAsync, deleteParagraphAsync, createParagraphAsync } from "../../../redux/paragraph";
import { getFullTranscriptionByIdAsync, splitParagraphAsync } from "../../../redux/transcription";
import { pushUndo, buildUndoEntry, selectUndoSyncToken, selectUndoSyncRows, selectUndoSyncIds, fetchSnapshots } from "../../../redux/undo";
import { SquarePen, RotateCcw, MoreVertical, Trash, Filter, Scissors, Bookmark, Copy, X } from 'lucide-react'
import { addToast } from "@heroui/react";
import { useParams } from "react-router-dom";
import AppButton from "../../common/AppButton";
import { markParagraphWord } from "../../../services/paragraphWords";
import { GetParagraphById } from "../../../services/paragraphs";
import useCorrectionWait from "../../../hooks/useCorrectionWait";
import {
  HIGHLIGHT_PALETTE,
  useHighlights,
  useHighlightsInRange,
} from "../../../contexts/HighlightsContext";

const SAVE_DELAY = 5000;
const SPLIT_VISIBILITY_MS = 5000;
// Word highlight lead: how far ahead of the audio the highlight should sit.
// 0 = highlight appears exactly when a word starts being spoken (recommended
// for accurate sync — previously 0.5s, which put the highlight 2-3 words
// ahead of what was being heard).
const HIGHLIGHT_LEAD_SECONDS = 0;
const CLICK_PIN_MS = 280;

const parseDurationToSeconds = (iso) => {
  if (typeof iso === "number") return iso;
  if (!iso || typeof iso !== "string") return NaN;
  const match = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?/);
  if (!match) return NaN;
  const hours = Number(match[1] || 0);
  const minutes = Number(match[2] || 0);
  const seconds = Number(match[3] || 0);
  return hours * 3600 + minutes * 60 + seconds;
};

const normalizeWordForMatch = (value = "") => {
  if (!value) return "";
  return value
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "");
};

const NestedAccordionItemContent = ({ 
  nestedRefs, 
  indexParagraphRef, 
  nestedSectionData, 
  openModal, 
  setOpenModal, 
  nestedKey, 
  correctedParagraphs, 
  setCorrectedParagraphs, 
  setSelectedSpeakers,
  previousParagraph,
  nextParagraph,
  speakers,
  currentTime = 0,
  isPlaying = false,
  onRequestSeek,
  promptMode = "soft",
  onOpenSpeakersModal,
}) => {
  const dispatch = useDispatch();
  const {isOpen, onOpen, onOpenChange} = useDisclosure();
  const {
    isOpen: isSplitConfirmOpen,
    onOpen: openSplitConfirm,
    onOpenChange: onSplitConfirmChange,
  } = useDisclosure();
  // After a split the raw text is available immediately, but the HARD/SOFT
  // passes that produce original_content / corrected_content are
  // regenerated by a background worker. `correction_status` tracks that:
  //   'pending' -> show a skeleton, block editing (the worker will
  //                overwrite anything typed here)
  //   'failed'  -> regeneration gave up; the columns currently hold the
  //                raw half, so warn that it is uncorrected
  const correctionStatus = nestedSectionData?.correction_status ?? null;
  const isCorrectionPending = correctionStatus === "pending";
  const isCorrectionFailed = correctionStatus === "failed";
  const [originalContentText, setOriginalContentText] = useState(nestedSectionData.original_content);
  const [correctedContentText, setCorrectedContentText] = useState(nestedSectionData.corrected_content ?? "");
  const [originalContent, setOriginalContent] = useState(nestedSectionData.original_content);
  const [correctedContent, setCorrectedContent] = useState(nestedSectionData.corrected_content);
  const [titleOfTheModal, setTitleOfTheModal] = useState("title")
  const [activatePrompt, setActivatePrompt] = useState(false)
  const [editOriginalContent, setEditOriginalContent] = useState(false)
  const [editCorrectedContent, setEditCorrectedContent] = useState(false);
  // Sync local view with global prompt mode
  const effectiveView = promptMode === "hard" ? "original" : "corrected";
  const [activeContentView, setActiveContentView] = useState(effectiveView);
  
  // Update local view when global mode changes
  useEffect(() => {
    setActiveContentView(effectiveView);
  }, [effectiveView]);
  const scrollRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isSplitMergeOpen, setIsSplitMergeOpen] = useState(false);
  const [splitAnchorIndex, setSplitAnchorIndex] = useState(null);
  const [isSplitBusy, setIsSplitBusy] = useState(false);
  const [splitMsgIndex, setSplitMsgIndex] = useState(0);

  const SPLIT_MESSAGES = [
    "Absatz wird geteilt…",
    "Bitte warten, das kann einen Moment dauern…",
    "Daten werden verarbeitet…",
    "KI analysiert den Text…",
    "Fast fertig, noch einen Moment…",
    "Wörter werden neu zugeordnet…",
    "Zusammenhänge werden geprüft…",
    "Zeitstempel werden angepasst…",
    "Sitzung wird aktualisiert…",
    "Noch ein paar Sekunden…",
    "Sprecherinformationen werden geprüft…",
    "Absatzgrenzen werden berechnet…",
    "Textstruktur wird analysiert…",
    "Änderungen werden vorbereitet…",
    "Transkription wird neu aufgebaut…",
    "Positionen werden aktualisiert…",
    "Datenbank wird synchronisiert…",
    "Qualitätsprüfung läuft…",
    "Abschnitte werden finalisiert…",
    "Gleich geschafft, bitte noch kurz warten…",
  ];

  useEffect(() => {
    if (!isSplitBusy) {
      setSplitMsgIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setSplitMsgIndex((prev) => (prev + 1) % SPLIT_MESSAGES.length);
    }, 10000);
    return () => clearInterval(interval);
  }, [isSplitBusy]);
  const [splitConfirmIndex, setSplitConfirmIndex] = useState(null);
  const [markAnchorIndex, setMarkAnchorIndex] = useState(null);
  const [markBusyId, setMarkBusyId] = useState(null);
  const { id } = useParams();
  const splitHoverTimerRef = useRef(null);
  const splitHideTimerRef = useRef(null);
  const markHoverTimerRef = useRef(null);
  const markHideTimerRef = useRef(null);
  const wordHighlightTimerRef = useRef(null);
  const activeWordIndexRef = useRef(null);
  // Tracks the audio time at which the current active word became active.
  // Used by the sync-debug overlay to show how long the current word has
  // been highlighted (useful for spotting "word stays highlighted forever"
  // or "every word visible for only 30 ms" bugs).
  const activeWordChangedAtRef = useRef(null);
  const previousTimeRef = useRef(null);
  const isPlayingRef = useRef(isPlaying);
  const previousIsPlayingRef = useRef(isPlaying);
  const isCurrentParagraphRef = useRef(false);
  const clickPinnedWordIndexRef = useRef(null);
  const clickPinnedUntilRef = useRef(0);
  const autosaveTimer = useRef(null);
  const savingInFlight = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveTick, setSaveTick] = useState(0); // to flash a "Saved" tick
  const rawContent = nestedSectionData?.raw_content || "";
  const getActiveContentText = useCallback(
    () =>
      activeContentView === "original"
        ? originalContentText
        : (correctedContentText ?? "").replace(/^\s/, ""),
    [activeContentView, correctedContentText, originalContentText]
  );
  const paragraphStart = Number(nestedSectionData?.start) || 0;
  const durationOverride = Number(nestedSectionData?.duration_seconds);
  const paragraphDuration = useMemo(() => {
    if (Number.isFinite(durationOverride) && durationOverride > 0) return durationOverride;
    const explicit = parseDurationToSeconds(nestedSectionData?.end);
    if (Number.isFinite(explicit) && explicit > 0) return explicit;
    const nextStart = Number(nextParagraph?.start);
    if (Number.isFinite(nextStart)) {
      return Math.max(nextStart - paragraphStart, 0);
    }
    return 0;
  }, [durationOverride, nestedSectionData?.end, nextParagraph, paragraphStart]);
  const paragraphWords = Array.isArray(nestedSectionData?.paragraph_words)
    ? nestedSectionData.paragraph_words
    : [];
  const normalizedParagraphWords = useMemo(() => {
    if (!Array.isArray(paragraphWords)) return [];
    return paragraphWords
      .map((word) => ({
        ...word,
        offsetSeconds:
          typeof word?.offsetSeconds === "number"
            ? word.offsetSeconds
            : parseDurationToSeconds(word?.word_offset),
        normalizedWord: normalizeWordForMatch(word?.word),
      }))
      .filter((word) => Number.isFinite(word.offsetSeconds))
      .sort((a, b) => a.offsetSeconds - b.offsetSeconds);
  }, [paragraphWords]);
  const lastWordOffset = useMemo(() => {
    if (!normalizedParagraphWords.length) return null;
    return normalizedParagraphWords[normalizedParagraphWords.length - 1].offsetSeconds;
  }, [normalizedParagraphWords]);
  const paragraphEndBoundary = useMemo(() => {
    const nextStart = Number(nextParagraph?.start);
    const explicitEnd =
      Number.isFinite(paragraphDuration) && paragraphDuration > 0
        ? paragraphStart + paragraphDuration
        : null;
    const derivedEnd = lastWordOffset != null ? lastWordOffset + 0.5 : null;
    let candidate = explicitEnd;
    if (derivedEnd != null && (candidate == null || derivedEnd > candidate)) {
      candidate = derivedEnd;
    }
    if (candidate == null) {
      return Number.isFinite(nextStart) ? nextStart : null;
    }
    if (Number.isFinite(nextStart)) {
      return Math.min(candidate, nextStart);
    }
    return candidate;
  }, [paragraphDuration, paragraphStart, nextParagraph, lastWordOffset]);
  const effectiveParagraphEnd = useMemo(() => {
    if (paragraphEndBoundary != null) return paragraphEndBoundary;
    if (lastWordOffset != null) return lastWordOffset + 0.5;
    return null;
  }, [paragraphEndBoundary, lastWordOffset]);
  const currentTimeBucket = useMemo(
    () => (Number.isFinite(currentTime) ? currentTime : undefined),
    [currentTime]
  );
  const tokenizedRawContent = useMemo(() => {
    if (!rawContent) {
      return {
        tokens: [],
        tokenWordIndices: [],
        totalWords: 0,
        normalizedWordsByIndex: [],
        charOffsetByWordIndex: [],
      };
    }
    const tokens = rawContent.match(/\S+|\s+/g) || [];
    const tokenWordIndices = [];
    const normalizedWordsByIndex = [];
    // Character offset in rawContent where each word starts. The token
    // regex captures whitespace too, so the tokens concatenate back to
    // rawContent exactly and a running sum of their lengths gives an
    // exact offset. The backend slices the text at this offset, which is
    // what makes the split lossless (no rebuilding from word rows).
    const charOffsetByWordIndex = [];
    let wordCounter = 0;
    let charCursor = 0;
    for (const segment of tokens) {
      if (/^\s+$/.test(segment)) {
        tokenWordIndices.push(null);
        charCursor += segment.length;
        continue;
      }
      const normalized = normalizeWordForMatch(segment);
      if (!normalized) {
        tokenWordIndices.push(null);
        charCursor += segment.length;
        continue;
      }
      tokenWordIndices.push(wordCounter);
      normalizedWordsByIndex[wordCounter] = normalized;
      charOffsetByWordIndex[wordCounter] = charCursor;
      wordCounter += 1;
      charCursor += segment.length;
    }
    return {
      tokens,
      tokenWordIndices,
      totalWords: wordCounter,
      normalizedWordsByIndex,
      charOffsetByWordIndex,
    };
  }, [rawContent]);
  const renderTokens = useMemo(() => {
    const tokens = tokenizedRawContent.tokens || [];
    const tokenWordIndices = tokenizedRawContent.tokenWordIndices || [];
    const nextWordIndexByToken = new Array(tokens.length).fill(null);
    let nextWordIndex = null;
    for (let i = tokens.length - 1; i >= 0; i -= 1) {
      const idx = tokenWordIndices[i];
      if (idx != null) {
        nextWordIndex = idx;
      }
      nextWordIndexByToken[i] = nextWordIndex;
    }
    const output = [];
    let lastWordIndex = null;
    for (let i = 0; i < tokens.length; i += 1) {
      const idx = tokenWordIndices[i];
      if (idx != null) {
        lastWordIndex = idx;
        output.push({ type: "word", text: tokens[i], wordIndex: idx, tokenIndex: i });
      } else {
        const splitIndex =
          lastWordIndex != null && nextWordIndexByToken[i] != null
            ? nextWordIndexByToken[i]
            : null;
        output.push({
          type: "space",
          text: tokens[i],
          splitIndex,
          tokenIndex: i,
        });
      }
    }
    return output;
  }, [tokenizedRawContent]);
  const totalWordTokens = tokenizedRawContent.totalWords;
  const dbWordBuckets = useMemo(() => {
    const map = new Map();
    for (const word of normalizedParagraphWords) {
      const key = word?.normalizedWord;
      if (!key) continue;
      if (!map.has(key)) {
        map.set(key, []);
      }
        map.get(key).push({
        word: word.word,
        offsetSeconds: word.offsetSeconds,
        id: word.id,
        word_id: word.word_id,
        wordId: word.wordId,
        mark: word.mark,
      });
    }
    return map;
  }, [normalizedParagraphWords]);
  const { wordOffsetsByIndex, matchedWordEntries } = useMemo(() => {
    if (!totalWordTokens) {
      return { wordOffsetsByIndex: [], matchedWordEntries: [] };
    }
    const offsets = new Array(totalWordTokens).fill(null);
    const matches = new Array(totalWordTokens).fill(null);
    if (!dbWordBuckets.size) {
      return { wordOffsetsByIndex: offsets, matchedWordEntries: matches };
    }
    // Text-identity FIFO: each rendered word looks up the bucket of DB rows
    // with the same normalized text and takes the next one (in time order).
    // No interpolation, no position guessing — uses DB offsets directly.
    const normalizedRawWords = tokenizedRawContent.normalizedWordsByIndex;
    const bucketCopies = new Map();
    dbWordBuckets.forEach((list, key) => {
      bucketCopies.set(key, list.slice());
    });
    for (let rawIdx = 0; rawIdx < totalWordTokens; rawIdx += 1) {
      const normalizedToken = normalizedRawWords[rawIdx];
      if (!normalizedToken) continue;
      const queue = bucketCopies.get(normalizedToken);
      if (queue && queue.length) {
        const entry = queue.shift();
        offsets[rawIdx] = Number.isFinite(entry?.offsetSeconds)
          ? entry.offsetSeconds
          : null;
        matches[rawIdx] = entry || null;
      }
    }
    return { wordOffsetsByIndex: offsets, matchedWordEntries: matches };
  }, [totalWordTokens, dbWordBuckets, tokenizedRawContent]);

  // --------------------------------------------------------------------
  // Highlights (multi-word, multi-color, shared, anchored to raw transcript)
  // --------------------------------------------------------------------
  const { addHighlight, requestDeleteHighlight } = useHighlights();
  // Only the highlights overlapping this paragraph's time range — keeps
  // per-word lookup cheap when there are hundreds of highlights per session.
  const paragraphHighlights = useHighlightsInRange(
    paragraphStart,
    effectiveParagraphEnd
  );
  // Map<wordIndex, highlight[]> for the current paragraph. A word belongs
  // to a highlight when its offsetSeconds falls within [start_offset,
  // end_offset] (inclusive on both ends so the last selected word is
  // always painted).
  const highlightsByWordIndex = useMemo(() => {
    const map = new Map();
    if (!paragraphHighlights.length) return map;
    for (let i = 0; i < wordOffsetsByIndex.length; i += 1) {
      const offset = wordOffsetsByIndex[i];
      if (!Number.isFinite(offset)) continue;
      const hits = paragraphHighlights.filter(
        (h) =>
          Number.isFinite(h.start_offset) &&
          Number.isFinite(h.end_offset) &&
          offset >= h.start_offset - 1e-6 &&
          offset <= h.end_offset + 1e-6
      );
      if (hits.length) map.set(i, hits);
    }
    return map;
  }, [paragraphHighlights, wordOffsetsByIndex]);

  // Drag-to-select state. Anchor + current pointer word index are tracked
  // in refs so pointermove doesn't force a React render on every pixel;
  // dragRange is only set when the inclusive [min,max] actually changes.
  const dragAnchorRef = useRef(null);
  const isDraggingRef = useRef(false);
  // Set to Date.now() + small window on drag end; any click within the
  // window is treated as the post-drag synthetic and dropped.
  const suppressClickUntilRef = useRef(null);
  const [dragRange, setDragRange] = useState(null);
  // Once the user releases the pointer on a multi-word selection, we open
  // a small color-picker popover. `anchorRect` is the DOM rect of the
  // last word so the popover can position itself.
  const [pendingHighlight, setPendingHighlight] = useState(null);
  const [isCreatingHighlight, setIsCreatingHighlight] = useState(false);
  const rawContentContainerRef = useRef(null);

  const wordIndexFromPointer = useCallback((event) => {
    // First try the event target. This works for non-captured events
    // (initial pointerdown, hover) where the target IS the element
    // under the cursor.
    let node = event.target?.closest?.("[data-word-index]");
    // Once we call setPointerCapture(container) below, every subsequent
    // pointermove arrives with event.target = container — and the
    // container has no data-word-index, so closest() walks UP and
    // returns null. Fall back to a real hit-test at the pointer's
    // viewport coordinates to find the word actually under the cursor.
    if (
      !node &&
      typeof document !== "undefined" &&
      Number.isFinite(event.clientX) &&
      Number.isFinite(event.clientY)
    ) {
      const hit = document.elementFromPoint(event.clientX, event.clientY);
      node = hit?.closest?.("[data-word-index]") || null;
    }
    if (!node) return null;
    const raw = node.getAttribute("data-word-index");
    const idx = Number(raw);
    return Number.isFinite(idx) ? idx : null;
  }, []);

  // Returns the word indices that have offsets, clamped to [start, end].
  // Used both for the live drag preview and for resolving the final range
  // into start_offset / end_offset / snippet.
  const wordsInDragRange = useCallback(
    (startIdx, endIdx) => {
      if (startIdx == null || endIdx == null) return [];
      const lo = Math.min(startIdx, endIdx);
      const hi = Math.max(startIdx, endIdx);
      const result = [];
      for (let i = lo; i <= hi; i += 1) {
        if (Number.isFinite(wordOffsetsByIndex[i])) {
          result.push(i);
        }
      }
      return result;
    },
    [wordOffsetsByIndex]
  );

  const handleRawPointerDown = useCallback(
    (event) => {
      // Left button only; ignore right-click and middle-click.
      if (event.button !== 0) return;
      const idx = wordIndexFromPointer(event);
      if (idx == null) return;
      // Words without a known offset can't anchor a highlight (we can't
      // store start/end times for them), so don't start a drag there.
      if (!Number.isFinite(wordOffsetsByIndex[idx])) return;
      dragAnchorRef.current = idx;
      isDraggingRef.current = false;
      setDragRange(null);
      // Do NOT setPointerCapture here. Capturing redirects the subsequent
      // click event from the word span up to the container, which would
      // break the existing click-to-seek on a single word. Capture is
      // requested later in pointermove the moment a real drag starts.
    },
    [wordIndexFromPointer, wordOffsetsByIndex]
  );

  const handleRawPointerMove = useCallback(
    (event) => {
      if (dragAnchorRef.current == null) return;
      const idx = wordIndexFromPointer(event);
      if (idx == null) return;
      if (idx !== dragAnchorRef.current && !isDraggingRef.current) {
        // First time we've crossed into a different word — this is a real
        // drag. Take the pointer now so pointermove/up keep flowing even
        // if the cursor leaves the container.
        isDraggingRef.current = true;
        try {
          event.currentTarget.setPointerCapture(event.pointerId);
        } catch (e) {
          /* not all environments support this; ignore */
        }
      }
      if (isDraggingRef.current) {
        // Suppress the native text selection that would otherwise appear
        // mid-drag. Safe to keep doing on every move — only matters while
        // a drag is active.
        event.preventDefault();
      }
      setDragRange((prev) => {
        const lo = Math.min(dragAnchorRef.current, idx);
        const hi = Math.max(dragAnchorRef.current, idx);
        if (prev && prev.lo === lo && prev.hi === hi) return prev;
        return { lo, hi };
      });
    },
    [wordIndexFromPointer]
  );

  const handleRawPointerUp = useCallback(
    (event) => {
      const anchor = dragAnchorRef.current;
      const wasDragging = isDraggingRef.current;
      dragAnchorRef.current = null;
      isDraggingRef.current = false;
      if (!wasDragging) {
        // Single click — let the existing word click handler take it.
        setDragRange(null);
        return;
      }
      // Drag completed: stop the click that the browser will emit next.
      suppressClickUntilRef.current = Date.now() + 350;
      const idx = wordIndexFromPointer(event);
      const endIdx = idx != null ? idx : (dragRange ? dragRange.hi : anchor);
      const indices = wordsInDragRange(anchor, endIdx);
      setDragRange(null);
      if (indices.length < 2) return; // need at least two words

      const startWord = indices[0];
      const endWord = indices[indices.length - 1];
      const startOffset = wordOffsetsByIndex[startWord];
      const endOffset = wordOffsetsByIndex[endWord];
      if (!Number.isFinite(startOffset) || !Number.isFinite(endOffset)) return;

      // Anchor the popover near the last selected word.
      const lastWordEl = rawContentContainerRef.current?.querySelector(
        `[data-word-index="${endWord}"]`
      );
      const containerRect =
        rawContentContainerRef.current?.getBoundingClientRect();
      const wordRect = lastWordEl?.getBoundingClientRect();
      const anchorPos =
        wordRect && containerRect
          ? {
              top: wordRect.bottom - containerRect.top + 6,
              left: Math.max(
                0,
                wordRect.left - containerRect.left - 12
              ),
            }
          : { top: 8, left: 8 };

      // Compose a plain-text snippet from the rendered tokens between the
      // bounds so the overview list can render it without re-tokenising.
      const snippetTokens = renderTokens
        .filter(
          (t) =>
            t.type === "word" &&
            t.wordIndex >= startWord &&
            t.wordIndex <= endWord
        )
        .map((t) => t.text);
      const snippet = snippetTokens.join(" ").trim().slice(0, 300);

      setPendingHighlight({
        startWord,
        endWord,
        startOffset,
        endOffset,
        snippet,
        anchorPos,
      });
    },
    [dragRange, renderTokens, wordIndexFromPointer, wordOffsetsByIndex, wordsInDragRange]
  );

  const handleRawPointerCancel = useCallback(() => {
    dragAnchorRef.current = null;
    isDraggingRef.current = false;
    setDragRange(null);
  }, []);

  // Open the color picker for a single word — used by the bookmark
  // button so a one-word highlight (which the drag gesture's 2-word
  // minimum doesn't allow) can still end up in the overview list and
  // on the waveform.
  const handleStartSingleWordHighlight = useCallback(
    (tokenWordIndex, anchorEl) => {
      const offset = wordOffsetsByIndex[tokenWordIndex];
      if (!Number.isFinite(offset)) return;
      const containerRect =
        rawContentContainerRef.current?.getBoundingClientRect();
      const anchorRect = anchorEl?.getBoundingClientRect?.();
      const anchorPos =
        anchorRect && containerRect
          ? {
              top: anchorRect.bottom - containerRect.top + 6,
              left: Math.max(0, anchorRect.left - containerRect.left - 12),
            }
          : { top: 8, left: 8 };
      const wordToken = renderTokens.find(
        (t) => t.type === "word" && t.wordIndex === tokenWordIndex
      );
      const snippet = (wordToken?.text || "").trim().slice(0, 300);
      setPendingHighlight({
        startWord: tokenWordIndex,
        endWord: tokenWordIndex,
        startOffset: offset,
        endOffset: offset,
        snippet,
        anchorPos,
      });
    },
    [renderTokens, wordOffsetsByIndex]
  );

  // Delete the topmost highlight overlapping a given word — used by the
  // inline ✕ that appears at the bookmark slot when hovering a word that
  // is part of a highlight. Defers to the provider-level confirm modal so
  // the look matches the rest of the app. Stacked highlights on the same
  // word get peeled off one click at a time (latest first), matching the
  // visual priority used by `persistedHighlightColor`.
  const handleDeleteHighlightAt = useCallback(
    (tokenWordIndex) => {
      const hits = highlightsByWordIndex.get(tokenWordIndex);
      if (!hits || !hits.length) return;
      const target = hits[hits.length - 1];
      requestDeleteHighlight(target.id);
    },
    [highlightsByWordIndex, requestDeleteHighlight]
  );

  const handleConfirmHighlight = useCallback(
    async (colorHex) => {
      if (!pendingHighlight || isCreatingHighlight) return;
      setIsCreatingHighlight(true);
      try {
        await addHighlight({
          startOffset: pendingHighlight.startOffset,
          endOffset: pendingHighlight.endOffset,
          color: colorHex,
          snippet: pendingHighlight.snippet,
        });
        setPendingHighlight(null);
      } catch (err) {
        console.error("Failed to create highlight:", err);
        addToast({
          title: "Hervorhebung konnte nicht gespeichert werden",
          description: err?.message || "Bitte erneut versuchen.",
          severity: "danger",
        });
      } finally {
        setIsCreatingHighlight(false);
      }
    },
    [addHighlight, isCreatingHighlight, pendingHighlight]
  );

  const isCurrentParagraph = useMemo(() => {
    if (effectiveParagraphEnd == null) {
      return currentTimeBucket >= paragraphStart;
    }
    return currentTimeBucket >= paragraphStart && currentTimeBucket < effectiveParagraphEnd;
  }, [currentTimeBucket, paragraphStart, effectiveParagraphEnd]);
  const [activeWordIndex, setActiveWordIndex] = useState(null);
  const wordTimeline = useMemo(() => {
    const entries = [];
    for (let i = 0; i < wordOffsetsByIndex.length; i += 1) {
      const start = wordOffsetsByIndex[i];
      if (Number.isFinite(start)) {
        entries.push({ wordIndex: i, start });
      }
    }
    return entries;
  }, [wordOffsetsByIndex]);
  const findTimelinePositionForTime = useCallback(
    (timeValue) => {
      if (!wordTimeline.length || !Number.isFinite(timeValue)) return -1;
      const adjustedTime = timeValue + HIGHLIGHT_LEAD_SECONDS;
      let pos = -1;
      for (let i = 0; i < wordTimeline.length; i += 1) {
        if (adjustedTime >= wordTimeline[i].start) {
          pos = i;
        } else {
          break;
        }
      }
      return pos;
    },
    [wordTimeline]
  );
  const clearWordHighlightTimer = useCallback(() => {
    if (wordHighlightTimerRef.current) {
      clearTimeout(wordHighlightTimerRef.current);
      wordHighlightTimerRef.current = null;
    }
  }, []);
  const scheduleWordHighlightFrom = useCallback(
    (startPos, delayMs) => {
      if (startPos < 0 || startPos >= wordTimeline.length) return;
      const safeDelay = Math.max(8, Math.floor(delayMs));
      clearWordHighlightTimer();
      wordHighlightTimerRef.current = setTimeout(() => {
        if (!isPlayingRef.current || !isCurrentParagraphRef.current) return;
        const nextEntry = wordTimeline[startPos];
        if (!nextEntry) return;
        activeWordIndexRef.current = nextEntry.wordIndex;
        setActiveWordIndex(nextEntry.wordIndex);
        if (startPos + 1 >= wordTimeline.length) return;
        const nextDelay = (wordTimeline[startPos + 1].start - nextEntry.start) * 1000;
        scheduleWordHighlightFrom(startPos + 1, nextDelay);
      }, safeDelay);
    },
    [clearWordHighlightTimer, wordTimeline]
  );
  const resyncWordHighlight = useCallback(
    (timeValue) => {
      const pos = findTimelinePositionForTime(timeValue);
      if (pos < 0) {
        activeWordIndexRef.current = null;
        setActiveWordIndex(null);
        clearWordHighlightTimer();
        return;
      }
      const currentEntry = wordTimeline[pos];
      activeWordIndexRef.current = currentEntry.wordIndex;
      setActiveWordIndex(currentEntry.wordIndex);
      if (!isPlayingRef.current || !isCurrentParagraphRef.current) {
        clearWordHighlightTimer();
        return;
      }
      if (pos + 1 >= wordTimeline.length) {
        clearWordHighlightTimer();
        return;
      }
      const delayToNext =
        (wordTimeline[pos + 1].start - (timeValue + HIGHLIGHT_LEAD_SECONDS)) * 1000;
      scheduleWordHighlightFrom(pos + 1, delayToNext);
    },
    [
      clearWordHighlightTimer,
      findTimelinePositionForTime,
      scheduleWordHighlightFrom,
      wordTimeline,
    ]
  );
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);
  // Word highlight: direct lookup against the live audio time on every
  // frame. The previous implementation chained `setTimeout` calls between
  // resync events, which drifted on long paragraphs and ignored playback
  // rate changes — leading to the highlight visibly running ahead of (or
  // behind) the spoken word. Direct lookup is drift-proof by construction:
  // the highlighted word is always the one whose start <= currentTime.
  useEffect(() => {
    isCurrentParagraphRef.current = isCurrentParagraph;
    if (!isCurrentParagraph) {
      clearWordHighlightTimer();
      if (activeWordIndexRef.current !== null) {
        activeWordIndexRef.current = null;
        activeWordChangedAtRef.current = null;
        setActiveWordIndex(null);
      }
      previousTimeRef.current = currentTimeBucket;
      previousIsPlayingRef.current = isPlaying;
      return;
    }
    if (!Number.isFinite(currentTimeBucket)) {
      previousIsPlayingRef.current = isPlaying;
      return;
    }

    // Clear any leftover scheduled timer from the legacy setTimeout path.
    clearWordHighlightTimer();

    const adjustedTime = currentTimeBucket + HIGHLIGHT_LEAD_SECONDS;
    let nextWordIndex = null;
    for (let i = 0; i < wordTimeline.length; i += 1) {
      if (adjustedTime >= wordTimeline[i].start) {
        nextWordIndex = wordTimeline[i].wordIndex;
      } else {
        break;
      }
    }

    if (nextWordIndex !== activeWordIndexRef.current) {
      activeWordIndexRef.current = nextWordIndex;
      activeWordChangedAtRef.current = Number.isFinite(currentTimeBucket)
        ? currentTimeBucket
        : null;
      setActiveWordIndex(nextWordIndex);
    }

    previousTimeRef.current = currentTimeBucket;
    previousIsPlayingRef.current = isPlaying;
  }, [
    clearWordHighlightTimer,
    currentTimeBucket,
    isCurrentParagraph,
    isPlaying,
    wordTimeline,
  ]);
  useEffect(() => {
    return () => {
      clearWordHighlightTimer();
    };
  }, [clearWordHighlightTimer]);

  // -------- Sync debug overlay --------
  // Enable by adding ?sync-debug=1 to the URL. Shows live mapping state for
  // the currently active paragraph: rendered token count, DB row count,
  // mapping mode, active word index, current audio time, active word's
  // offset, and the delta (highlight lead/lag vs. audio).
  const syncDebugEnabled = useMemo(() => {
    if (typeof window === "undefined") return false;
    try {
      const params = new URLSearchParams(window.location.search);
      const v = params.get("sync-debug");
      return v === "1" || v === "true";
    } catch {
      return false;
    }
  }, []);

  const syncDebugInfo = useMemo(() => {
    if (!syncDebugEnabled) return null;
    const dbCount = normalizedParagraphWords.length;
    const rendered = totalWordTokens;
    let mode = "no-db";
    if (dbCount > 0) {
      const ratio = dbCount / Math.max(1, rendered);
      mode =
        ratio >= 0.75 && ratio <= 1.25
          ? `position-interp (db/rend ratio ${ratio.toFixed(2)})`
          : `text-bucket-fallback (db/rend ratio ${ratio.toFixed(2)})`;
    }
    const activeOffset =
      activeWordIndex != null ? wordOffsetsByIndex[activeWordIndex] : null;
    const delta =
      Number.isFinite(activeOffset) && Number.isFinite(currentTimeBucket)
        ? currentTimeBucket - activeOffset
        : null;
    // How long has the current active word been highlighted? Computed from
    // the audio time delta since the last word change. Useful for spotting
    // "word never changes" (huge value) or "every word visible for 30 ms"
    // (tiny value) bugs.
    const changedAt = activeWordChangedAtRef.current;
    const shownForSeconds =
      Number.isFinite(changedAt) && Number.isFinite(currentTimeBucket)
        ? currentTimeBucket - changedAt
        : null;
    // What's the expected duration of the active word? The gap between its
    // offset and the next word's offset in the timeline. Compare with
    // shownForSeconds — they should be close as time progresses.
    let expectedWordDuration = null;
    if (activeWordIndex != null) {
      const pos = wordTimeline.findIndex(
        (e) => e.wordIndex === activeWordIndex
      );
      if (pos >= 0 && pos + 1 < wordTimeline.length) {
        expectedWordDuration =
          wordTimeline[pos + 1].start - wordTimeline[pos].start;
      }
    }
    return {
      paragraphId: nestedSectionData?.id,
      paragraphStart,
      effectiveParagraphEnd,
      rendered,
      dbCount,
      mode,
      activeWordIndex,
      activeOffset,
      currentTime: currentTimeBucket,
      delta,
      timelineLen: wordTimeline.length,
      shownForSeconds,
      expectedWordDuration,
    };
  }, [
    syncDebugEnabled,
    normalizedParagraphWords.length,
    totalWordTokens,
    activeWordIndex,
    wordOffsetsByIndex,
    currentTimeBucket,
    nestedSectionData?.id,
    paragraphStart,
    effectiveParagraphEnd,
    wordTimeline,
  ]);

  const seekToWordIndex = useCallback(
    (wordIndex) => {
      if (typeof onRequestSeek !== "function") return;
      if (typeof wordIndex !== "number" || wordIndex < 0) return;
      const targetTime = wordOffsetsByIndex[wordIndex];
      if (Number.isFinite(targetTime)) {
        onRequestSeek(targetTime);
      }
    },
    [onRequestSeek, wordOffsetsByIndex]
  );

  const resolveSplitWordId = useCallback(
    (splitIndex) => {
      if (!Array.isArray(matchedWordEntries)) return null;
      for (let i = splitIndex; i < matchedWordEntries.length; i += 1) {
        const entry = matchedWordEntries[i];
        const id = entry?.id ?? entry?.word_id ?? entry?.wordId;
        if (id != null) return id;
      }
      return null;
    },
    [matchedWordEntries]
  );

  // Wait while THIS paragraph's corrections are being regenerated, so the
  // skeleton is replaced as soon as the worker lands.
  //
  // Driven off `isCorrectionPending` rather than kicked off imperatively
  // by the split handler: the split inserts a paragraph, which re-renders
  // the list and can unmount the component that started the wait. Keying
  // it to state means any mounted pending paragraph waits, and the wait
  // stops by itself the moment the status clears.
  //
  // Each check reads only this paragraph; the session, with all its words,
  // is reloaded once the status has settled. It used to be reloaded every
  // 3 seconds and only for 2 minutes, so a slower correction left the
  // skeleton up until a page reload. The wait now lasts until the backend
  // settles the status, which it does within its 10-minute lock.
  useCorrectionWait(
    isCorrectionPending && Boolean(id),
    () => GetParagraphById(nestedSectionData.id),
    () => dispatch(getFullTranscriptionByIdAsync(id))
  );

  // Adopt server-side content when it changes underneath us — without this
  // the textareas keep whatever `useState` seeded at mount, so a
  // regenerated correction only appeared after a full page reload. Local
  // edits are preserved: we only overwrite when the current value still
  // matches the last value we took from the server.
  const lastSyncedContentRef = useRef({
    original: nestedSectionData.original_content,
    corrected: nestedSectionData.corrected_content ?? "",
  });
  useEffect(() => {
    const incomingOriginal = nestedSectionData.original_content;
    const incomingCorrected = nestedSectionData.corrected_content ?? "";
    if (incomingOriginal !== lastSyncedContentRef.current.original) {
      const previous = lastSyncedContentRef.current.original;
      setOriginalContentText((local) =>
        local === previous ? incomingOriginal : local
      );
      lastSyncedContentRef.current.original = incomingOriginal;
    }
    if (incomingCorrected !== lastSyncedContentRef.current.corrected) {
      const previous = lastSyncedContentRef.current.corrected;
      setCorrectedContentText((local) =>
        local === previous ? incomingCorrected : local
      );
      lastSyncedContentRef.current.corrected = incomingCorrected;
    }
  }, [nestedSectionData.original_content, nestedSectionData.corrected_content]);

  // Undo resync. The guard above only overwrites local text when the value
  // arriving in props changed. After an undo it often has not: the edited
  // text lived only in local state, so props still hold the pre-edit value
  // that undo just restored on the server. Nothing changes, the effect does
  // not fire, and the textarea keeps showing the edit that was just undone.
  // This forces the read-through whenever an undo completes.
  const undoSyncToken = useSelector(selectUndoSyncToken);
  const undoSyncRows = useSelector(selectUndoSyncRows);
  const undoSyncIds = useSelector(selectUndoSyncIds);
  const undoSyncSeenRef = useRef(undoSyncToken);
  useEffect(() => {
    if (undoSyncToken === undoSyncSeenRef.current) return;
    undoSyncSeenRef.current = undoSyncToken;

    // Prefer the content the undo carried with it. Reading from props here
    // is a race: the restored rows reach this component through the store,
    // the regrouping effect and SessionView's local state, none of which has
    // run yet. The right-hand field would keep the pre-undo text until a
    // page reload, which is exactly what it did.
    const myId = Number(nestedSectionData.id);
    let incomingOriginal;
    let incomingCorrected;
    if (undoSyncRows) {
      const mine = undoSyncRows.find((r) => Number(r.id) === myId);
      if (!mine) return; // this paragraph was not part of the undo
      incomingOriginal = mine.original_content;
      incomingCorrected = mine.corrected_content ?? "";
    } else {
      // Props are authoritative at this point (post-refetch). Only the
      // paragraphs the undo touched need to re-read.
      if (undoSyncIds && !undoSyncIds.includes(myId)) return;
      incomingOriginal = nestedSectionData.original_content;
      incomingCorrected = nestedSectionData.corrected_content ?? "";
    }

    setOriginalContentText(incomingOriginal);
    setCorrectedContentText(incomingCorrected);
    // Keep the saved-baseline in step too, or the autosave effect sees the
    // restored text as an unsaved edit and writes it straight back.
    setOriginalContent(incomingOriginal);
    setCorrectedContent(incomingCorrected);
    lastSyncedContentRef.current = {
      original: incomingOriginal,
      corrected: incomingCorrected,
    };
  }, [undoSyncToken, undoSyncRows, undoSyncIds, nestedSectionData.id]);

  const handleSplitExecute = useCallback(
    async (splitIndex) => {
      if (!nestedSectionData?.id || !Array.isArray(paragraphWords) || !paragraphWords.length) {
        return;
      }
      // Refuse to split a paragraph whose corrections are still being
      // rebuilt — the worker would overwrite whichever half it lands on.
      if (isCorrectionPending) {
        addToast({
          title: "Absatz wird gerade nachbearbeitet",
          description: "Bitte warten Sie, bis die Korrektur fertig ist.",
          color: "warning",
        });
        return;
      }
      // The character offset is what the backend actually splits on — it
      // maps 1:1 onto the text the user clicked, so the split is exact and
      // preserves punctuation. `split_word_id` is still sent so the backend
      // can derive the new paragraph's timestamp (and as a fallback for
      // older deployments).
      const splitCharOffset =
        tokenizedRawContent.charOffsetByWordIndex?.[splitIndex];
      const splitWordId = resolveSplitWordId(splitIndex);
      if (!Number.isFinite(splitCharOffset) && !splitWordId) {
        addToast({
          title: "An dieser Stelle kann nicht geteilt werden",
          color: "danger",
        });
        return;
      }
      setIsSplitBusy(true);
      // Snapshot from the server — see the note in fetchSnapshots.
      const [undoBefore] = await fetchSnapshots([nestedSectionData.id]);
      const action = await dispatch(
        splitParagraphAsync({
          paragraph_id: nestedSectionData.id,
          split_char_offset: Number.isFinite(splitCharOffset)
            ? splitCharOffset
            : undefined,
          split_word_id: splitWordId,
          paragraph_words: paragraphWords,
        })
      );
      if (splitParagraphAsync.fulfilled.match(action)) {
        // Undo restores the pre-split paragraph and removes the new half.
        // Guard is "existence" rather than "content": the background AI
        // regeneration rewrites both halves within seconds, so a content
        // check would refuse every undo shortly after the split.
        const updated = action.payload?.updated_paragraph;
        const created = action.payload?.created_paragraph;
        if (updated && created && undoBefore) {
          dispatch(pushUndo(buildUndoEntry({
            type: "split",
            label: "Absatz teilen",
            sessionId: id,
            guard: "existence",
            before: [undoBefore],
            after: [updated, created],
          })));
        }
        addToast({
          title: "Absatz geteilt",
          description: "Die Korrektur wird im Hintergrund neu erstellt.",
          color: "success",
        });
        // Both halves come back with correction_status='pending', which
        // starts the polling effect above — no need to kick it off here.
        await dispatch(getFullTranscriptionByIdAsync(id));
      } else {
        // Show the backend's reason ("Split must leave words on both
        // sides", ...) — a bare "failed" gives the user nothing to act on.
        addToast({
          title: "Teilen fehlgeschlagen",
          description: action?.error?.message || undefined,
          color: "danger",
        });
      }
      setIsSplitBusy(false);
      setSplitAnchorIndex(null);
    },
    [
      dispatch,
      id,
      isCorrectionPending,
      nestedSectionData,
      paragraphWords,
      resolveSplitWordId,
      tokenizedRawContent,
    ]
  );

  const handleToggleMark = useCallback(
    async (wordIndex) => {
      if (!Array.isArray(matchedWordEntries)) return;
      const entry = matchedWordEntries[wordIndex];
      const wordId = entry?.id ?? entry?.word_id ?? entry?.wordId;
      if (!wordId) return;
      if (markBusyId === wordId) return;
      const nextMark = !entry?.mark;
      setMarkBusyId(wordId);
      const result = await markParagraphWord(wordId, nextMark);
      if (result && typeof result === "object" && result.id) {
        addToast({
          title: nextMark ? "Wort markiert" : "Markierung entfernt",
          color: "success",
        });
        await dispatch(getFullTranscriptionByIdAsync(id));
      } else {
        addToast({
          title: "Wortmarkierung konnte nicht aktualisiert werden",
          color: "danger",
        });
      }
      setMarkBusyId(null);
    },
    [dispatch, id, markBusyId, matchedWordEntries]
  );

  const requestSplitConfirm = useCallback(
    (splitIndex) => {
      if (splitIndex == null || isSplitBusy) return;
      setSplitConfirmIndex(splitIndex);
      openSplitConfirm();
    },
    [isSplitBusy, openSplitConfirm]
  );

  const handleCopyActiveText = useCallback(async () => {
    const text = getActiveContentText();
    if (!text) {
      addToast({
        title: "Kein Text zum Kopieren",
        color: "warning",
      });
      return;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const temp = document.createElement("textarea");
        temp.value = text;
        temp.setAttribute("readonly", "");
        temp.style.position = "fixed";
        temp.style.top = "-1000px";
        temp.style.opacity = "0";
        document.body.appendChild(temp);
        temp.focus();
        temp.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(temp);
        if (!ok) throw new Error("copy failed");
      }
      addToast({
        title: "Text kopiert",
        color: "success",
      });
    } catch (error) {
      console.error("Failed to copy text", error);
      addToast({
        title: "Kopieren fehlgeschlagen",
        color: "danger",
      });
    }
  }, [getActiveContentText]);

  const startSplitHover = useCallback(
    (splitIndex) => {
      if (isSplitBusy || splitIndex == null) return;
      if (splitHoverTimerRef.current) {
        clearTimeout(splitHoverTimerRef.current);
      }
      setSplitAnchorIndex(splitIndex);
      if (splitHideTimerRef.current) {
        clearTimeout(splitHideTimerRef.current);
      }
      splitHideTimerRef.current = setTimeout(() => {
        setSplitAnchorIndex((current) => (current === splitIndex ? null : current));
      }, SPLIT_VISIBILITY_MS);
    },
    [isSplitBusy]
  );

  const endSplitHover = useCallback(
    (splitIndex) => {
      if (splitHoverTimerRef.current) {
        clearTimeout(splitHoverTimerRef.current);
      }
      // Keep the button visible until its timeout expires.
    },
    []
  );

  useEffect(() => {
    return () => {
      if (splitHoverTimerRef.current) {
        clearTimeout(splitHoverTimerRef.current);
      }
      if (splitHideTimerRef.current) {
        clearTimeout(splitHideTimerRef.current);
      }
      if (markHoverTimerRef.current) {
        clearTimeout(markHoverTimerRef.current);
      }
      if (markHideTimerRef.current) {
        clearTimeout(markHideTimerRef.current);
      }
    };
  }, []);

  const isInteractive = typeof onRequestSeek === "function";
  const handleTokenClick = useCallback(
    (event) => {
      if (!isInteractive) return;
      // Suppress the synthetic click that follows a drag-to-select gesture
      // so we don't seek the audio to the last word of a freshly created
      // highlight. The window is short — only the click immediately after
      // pointerup gets swallowed.
      if (
        suppressClickUntilRef.current != null &&
        Date.now() < suppressClickUntilRef.current
      ) {
        suppressClickUntilRef.current = null;
        return;
      }
      const { wordIndex } = event.currentTarget.dataset || {};
      const parsedIndex = Number(wordIndex);
      if (!Number.isFinite(parsedIndex)) return;
      clickPinnedWordIndexRef.current = parsedIndex;
      clickPinnedUntilRef.current = Date.now() + CLICK_PIN_MS;
      setActiveWordIndex(parsedIndex);
      seekToWordIndex(parsedIndex);
    },
    [isInteractive, seekToWordIndex]
  );
  const handleTokenKeyDown = useCallback(
    (event) => {
      if (!isInteractive) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleTokenClick(event);
      }
    },
    [handleTokenClick, isInteractive]
  );
  const highlightedRawContent = useMemo(() => {
    if (!renderTokens.length) return null;
    // While a drag-to-highlight is in progress, suppress all the hover-
    // driven scissors/bookmark UI: they would otherwise pop in under the
    // cursor, intercept pointer events at the word gaps (the scissors
    // hover-zone has no data-word-index, so closest() returns null and
    // the drag would lose the cursor), and force needless re-renders.
    const isDragging = dragRange != null;
    return renderTokens.map((token) => {
      if (token.type === "space") {
        const splitIndex =
          typeof token.splitIndex === "number" && token.splitIndex > 0
            ? token.splitIndex
            : null;
        const eligibleSplit =
          splitIndex != null && splitIndex < totalWordTokens;
        const showSplit =
          !isDragging && eligibleSplit && splitAnchorIndex === splitIndex;
        return (
          <span
            key={`raw-gap-${token.tokenIndex}`}
            className="relative inline-block"
          >
            <span
              className={classNames(
                "absolute inset-y-0 left-1/2 w-5 -translate-x-1/2",
                isDragging ? "pointer-events-none" : undefined
              )}
              onMouseEnter={() => {
                if (isDragging || !eligibleSplit) return;
                startSplitHover(splitIndex);
              }}
              onMouseLeave={() => endSplitHover(splitIndex)}
            />
            {token.text}
            {showSplit ? (
              <button
                type="button"
                className="absolute left-1/2 top-1/2 -translate-y-1/2 rounded-full bg-gray-800 text-white shadow-sm p-1 translate-x-4"
                onClick={() => {
                  if (splitIndex == null || isSplitBusy) return;
                  requestSplitConfirm(splitIndex);
                }}
                aria-label="Split paragraph"
              >
                {/* <Scissors className="w-3 h-3" /> */}
              </button>
            ) : null}
          </span>
        );
      }
      const tokenWordIndex = token.wordIndex;
      const isPinnedActive =
        clickPinnedWordIndexRef.current != null &&
        Date.now() < clickPinnedUntilRef.current &&
        tokenWordIndex === clickPinnedWordIndexRef.current;
      const isActive =
        isPinnedActive ||
        (activeWordIndex != null && tokenWordIndex === activeWordIndex);
      const isMarked = Boolean(matchedWordEntries?.[tokenWordIndex]?.mark);
      const entryId =
        matchedWordEntries?.[tokenWordIndex]?.id ??
        matchedWordEntries?.[tokenWordIndex]?.word_id ??
        matchedWordEntries?.[tokenWordIndex]?.wordId ??
        null;
      const wordSplitIndex = tokenWordIndex + 1;
      const wordSplitEligible = wordSplitIndex > 0 && wordSplitIndex < totalWordTokens;
      const showWordSplit =
        !isDragging &&
        wordSplitEligible &&
        splitAnchorIndex === wordSplitIndex;
      const wordHighlights = highlightsByWordIndex.get(tokenWordIndex);
      const persistedHighlightColor =
        wordHighlights && wordHighlights.length
          ? wordHighlights[wordHighlights.length - 1].color
          : null;
      // The slot at -left-6 of the word is the per-word action target.
      // It swaps between two affordances depending on whether the word
      // already belongs to a highlight:
      //   • not highlighted  → bookmark icon (add a 1-word highlight)
      //   • highlighted      → red ✕ icon  (delete that highlight)
      // Both are gated on hover (markAnchorIndex matches) and hidden
      // during a drag so the z-20 button doesn't intercept pointer
      // events at the word gap and stall the selection.
      const hoveringWord =
        !isDragging && markAnchorIndex === tokenWordIndex;
      const wordIsHighlighted = persistedHighlightColor != null;
      const showMarkButton =
        hoveringWord &&
        !wordIsHighlighted &&
        entryId != null &&
        Number.isFinite(wordOffsetsByIndex[tokenWordIndex]);
      const showDeleteHighlightButton = hoveringWord && wordIsHighlighted;
      const activeBgClass = isMarked ? "bg-amber-400" : "bg-secondaryContainer";
      const inDragPreview =
        dragRange != null &&
        tokenWordIndex >= dragRange.lo &&
        tokenWordIndex <= dragRange.hi;
      return (
        <span
          key={`raw-word-${token.tokenIndex}`}
          role={isInteractive ? "button" : undefined}
          tabIndex={isInteractive ? 0 : undefined}
          data-word-index={tokenWordIndex}
          onClick={isInteractive ? handleTokenClick : undefined}
          onKeyDown={isInteractive ? handleTokenKeyDown : undefined}
          onMouseEnter={() => {
            // Don't touch hover state while a drag-highlight is in progress;
            // re-arming markAnchorIndex on every crossed word forces a
            // re-render per word and visually flickers the bookmark popup
            // along the drag path.
            if (isDragging) return;
            if (wordSplitEligible) {
              startSplitHover(wordSplitIndex);
            }
            if (markHoverTimerRef.current) {
              clearTimeout(markHoverTimerRef.current);
            }
            setMarkAnchorIndex(tokenWordIndex);
            if (markHideTimerRef.current) {
              clearTimeout(markHideTimerRef.current);
            }
            markHideTimerRef.current = setTimeout(() => {
              setMarkAnchorIndex((current) =>
                current === tokenWordIndex ? null : current
              );
            }, SPLIT_VISIBILITY_MS);
          }}
          onMouseLeave={() => {
            if (isDragging) return;
            endSplitHover(wordSplitIndex);
            if (markHoverTimerRef.current) {
              clearTimeout(markHoverTimerRef.current);
            }
          }}
          className={classNames(
            "relative inline-flex transition-colors",
            isInteractive
              ? "cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-secondary"
              : undefined
          )}
        >
          {/* Layer order (bottom → top): mark (bookmark), persisted
              highlight, drag preview, active (audio-playing). Highlight
              sits above the bookmark so a highlighted+bookmarked word
              still reads as highlighted; the active overlay tops them
              while the word is being spoken. */}
          {isMarked && !isActive ? (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 rounded-sm opacity-100 bg-amber-300"
            />
          ) : null}
          {persistedHighlightColor ? (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 rounded-sm"
              style={{ backgroundColor: persistedHighlightColor, opacity: 0.55 }}
            />
          ) : null}
          {inDragPreview ? (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 rounded-sm bg-blue-300/40 ring-1 ring-blue-400/60"
            />
          ) : null}
          <span
            aria-hidden="true"
            className={classNames(
              "pointer-events-none absolute inset-0 rounded-sm transition-opacity duration-150",
              isActive ? `opacity-100 ${activeBgClass}` : "opacity-0"
            )}
          />
          <span
            className={classNames(
              "relative z-10",
              isActive ? "text-onSecondaryContainer" : undefined
            )}
          >
            {token.text}
          </span>
          {showMarkButton ? (
            <button
              type="button"
              className="absolute -left-6 top-1/2 -translate-y-1/2 rounded-full shadow-sm p-1 z-20 bg-gray-800 text-white"
              onMouseEnter={() => {
                if (markHideTimerRef.current) {
                  clearTimeout(markHideTimerRef.current);
                }
              }}
              onMouseLeave={() => {
                if (markHideTimerRef.current) {
                  clearTimeout(markHideTimerRef.current);
                }
                markHideTimerRef.current = setTimeout(() => {
                  setMarkAnchorIndex((current) =>
                    current === tokenWordIndex ? null : current
                  );
                }, SPLIT_VISIBILITY_MS);
              }}
              onClick={(event) => {
                event.stopPropagation();
                handleStartSingleWordHighlight(
                  tokenWordIndex,
                  event.currentTarget
                );
              }}
              aria-label="Hervorhebung hinzufügen"
              title="Hervorhebung hinzufügen"
            >
              <Bookmark className="w-3 h-3" />
            </button>
          ) : null}
          {showDeleteHighlightButton ? (
            <button
              type="button"
              className="absolute -left-6 top-1/2 -translate-y-1/2 rounded-full shadow-sm p-1 z-20 bg-red-600 text-white hover:bg-red-700"
              onMouseEnter={() => {
                if (markHideTimerRef.current) {
                  clearTimeout(markHideTimerRef.current);
                }
              }}
              onMouseLeave={() => {
                if (markHideTimerRef.current) {
                  clearTimeout(markHideTimerRef.current);
                }
                markHideTimerRef.current = setTimeout(() => {
                  setMarkAnchorIndex((current) =>
                    current === tokenWordIndex ? null : current
                  );
                }, SPLIT_VISIBILITY_MS);
              }}
              onClick={(event) => {
                event.stopPropagation();
                handleDeleteHighlightAt(tokenWordIndex);
              }}
              aria-label="Hervorhebung löschen"
              title="Hervorhebung löschen"
            >
              <X className="w-3 h-3" />
            </button>
          ) : null}
          {showWordSplit ? (
            <button
              type="button"
              className="absolute -right-6 top-1/2 -translate-y-1/2 rounded-full bg-gray-800 text-white shadow-sm p-1 z-20"
              onClick={(event) => {
                event.stopPropagation();
                if (!wordSplitEligible || isSplitBusy) return;
                requestSplitConfirm(wordSplitIndex);
              }}
              aria-label="Split paragraph"
            >
              <Scissors className="w-3 h-3" />
            </button>
          ) : null}
        </span>
      );
    });
  }, [
    renderTokens,
    activeWordIndex,
    isInteractive,
    handleTokenClick,
    handleTokenKeyDown,
    requestSplitConfirm,
    startSplitHover,
    endSplitHover,
    handleStartSingleWordHighlight,
    handleDeleteHighlightAt,
    wordOffsetsByIndex,
    nestedSectionData?.id,
    matchedWordEntries,
    splitAnchorIndex,
    isSplitBusy,
    totalWordTokens,
    markAnchorIndex,
    highlightsByWordIndex,
    dragRange,
  ]);
    

  // handleRegeneration only writes local state; the debounced autosave is
  // what persists it, so the undo entry is recorded there. This flag lets
  // that entry say what actually happened instead of "Textänderung".
  const pendingRegenLabelRef = useRef(null);

  const handleSave = async () => {
    // make it return a promise we can await
    savingInFlight.current = true;
    setIsSaving(true);

    // Snapshot from the server: the view normalises `start` to a number and
    // writing that back would destroy the ISO timestamp.
    const [undoBefore] = await fetchSnapshots([nestedSectionData.id]);

    await dispatch(editParagraphAsync({
      id: nestedSectionData.id,
      payload: {
        original_content: originalContentText,
        corrected_content: correctedContentText
      }
    }));

    const undoLabel = pendingRegenLabelRef.current || "Textänderung";
    pendingRegenLabelRef.current = null;

    if (undoBefore) dispatch(pushUndo(buildUndoEntry({
      type: "edit",
      label: undoLabel,
      sessionId: nestedSectionData?.transcription_id,
      before: [undoBefore],
      after: [{
        ...undoBefore,
        original_content: originalContentText,
        corrected_content: correctedContentText,
      }],
    })));

    setCorrectedParagraphs(prev => ({
      ...prev,
      [nestedSectionData.id]: {
        original_content: originalContentText,
        corrected_content: correctedContentText
      },
    }));

    setOriginalContent(originalContentText);
    setCorrectedContent(correctedContentText);

    setIsSaving(false);
    setSaveTick((n) => n + 1); // trigger "Saved" flash
    savingInFlight.current = false;
    addToast({
      title: "Absätze gespeichert",
      color: "success"
    });
  };

  // NEW: debounced autosave effect
  useEffect(() => {
    // clear any prior timer
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);

    const hasUnsavedChanges =
      originalContentText !== originalContent ||
      correctedContentText !== correctedContent;

    if (!hasUnsavedChanges) return;

    autosaveTimer.current = setTimeout(async () => {
      // avoid overlapping saves
      if (savingInFlight.current) return;
      await handleSave();
    }, SAVE_DELAY);

    // cleanup on change/unmount
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [originalContentText, correctedContentText]); 
    


  const handleRegeneration = async () => {
    const inputText = promptMode === "hard" ? originalContentText : correctedContentText;
    if (!inputText || !inputText.trim()) {
      addToast({ title: "Kein Inhalt zum Regenerieren vorhanden", color: "danger" });
      return;
    }
    const payload = {
      original_content: inputText,
      paragraph_id: nestedSectionData.id,
      hard_case: promptMode === "hard",
    };

    const action = await dispatch(regenerateParagraphContentAsync(payload));

    // Only a string reply may reach the text field: whatever lands there is
    // autosaved over the paragraph a moment later.
    const regeneratedText = action.payload?.response;
    if (!regenerateParagraphContentAsync.fulfilled.match(action) || typeof regeneratedText !== "string") {
      console.error("Regeneration failed:", action.error?.message || action.payload);
      addToast({
        title: "Neugenerierung fehlgeschlagen",
        description: action.error?.message || "Der Text wurde nicht verändert.",
        color: "danger",
      });
      return;
    }

    pendingRegenLabelRef.current =
      promptMode === "hard"
        ? "KI-Neugenerierung (Hard Facts)"
        : "KI-Neugenerierung (Soft Facts)";
    if (promptMode === "hard") {
      setOriginalContentText(regeneratedText);
    } else {
      setCorrectedContentText(regeneratedText);
    }

    // scroll to top
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }

    setEditCorrectedContent(true)

    addToast({
      title: "Absatz neu generiert",
      color: "success"
    });
  };
  

  useEffect(() => {
    if (openModal === nestedKey) {
      setOpenModal(null);
      setOriginalContentText(nestedSectionData.original_content);
      setCorrectedContentText(nestedSectionData.corrected_content ?? "");
      onOpen();
    }
  }, [openModal]);

  useEffect(() => {
    if(nestedSectionData.id in correctedParagraphs){
      setOriginalContent(correctedParagraphs[nestedSectionData.id]["original_content"])
      setCorrectedContent(correctedParagraphs[nestedSectionData.id]["corrected_content"])
      setOriginalContentText(correctedParagraphs[nestedSectionData.id]["original_content"])
      setCorrectedContentText(correctedParagraphs[nestedSectionData.id]["corrected_content"] ?? "")
    }
  }, []);

  const openModalChangeContent = (title) =>{
    setTitleOfTheModal(title);
    onOpen()
  }

  const handleSplitMergeSave = async (updates) => {
    try {
      const hmsToIsoDuration = (hms) => {
        if (!hms || typeof hms !== "string") return undefined;
        const m = hms.match(/^(\d{2}):(\d{2}):(\d{2})$/);
        if (!m) return undefined;
        const hh = Number(m[1]);
        const mm = Number(m[2]);
        const ss = Number(m[3]);
        if (!Number.isFinite(hh) || !Number.isFinite(mm) || !Number.isFinite(ss)) return undefined;
        const parts = [];
        if (hh > 0) parts.push(`${hh}H`);
        if (mm > 0) parts.push(`${mm}M`);
        parts.push(`${ss}S`);
        return `PT${parts.join("")}`;
      };

      for (const update of updates) {
        if (update.type === 'update') {
          await dispatch(editParagraphAsync({
            id: update.paragraphId,
            payload: {
              ...(update.raw_content !== undefined ? { raw_content: update.raw_content } : {}),
              original_content: update.original_content,
              corrected_content: update.corrected_content
            }
          }));
        } else if (update.type === 'delete') {
          await dispatch(deleteParagraphAsync(update.paragraphId));
        } else if (update.type === 'create') {
          let speakerNumber;
          if (Array.isArray(speakers)) {
            const match = speakers.find((s) => s?.key === update.speaker_name || s?.label === update.speaker_name);
            if (match && (typeof match.value === 'number' || (typeof match.value === 'string' && match.value.trim() !== ''))) {
              const parsed = Number(match.value);
              speakerNumber = Number.isFinite(parsed) ? parsed : undefined;
            }
          }
          if (speakerNumber === undefined && typeof update.speaker_name === 'string') {
            const m = update.speaker_name.match(/(\d+)/);
            if (m) {
              const parsed = Number(m[1]);
              if (Number.isFinite(parsed)) speakerNumber = parsed;
            }
          }

          const isoStart = hmsToIsoDuration(update.timestamp);
          const isoEnd = hmsToIsoDuration(update.timestamp);

          await dispatch(createParagraphAsync({
            transcription_id: id,
            speaker_name: update.speaker_name,
            start: isoStart,
            raw_content: update.raw_content,
            original_content: update.original_content,
            corrected_content: update.corrected_content,
            end: isoEnd,
            ...(speakerNumber ? { speaker_number: speakerNumber } : {})
          }));
        }
      }
      
      addToast({
        title: "Änderungen gespeichert",
        color: "success"
      });
      // Refresh full transcription to reflect newly created/updated/deleted paragraphs
      await dispatch(getFullTranscriptionByIdAsync(id));
    } catch (error) {
      console.error('Error saving split/merge changes:', error);
      addToast({
        title: "Fehler beim Speichern der Änderungen",
        color: "danger"
      });
    }
  }





  return(    
    <>
      {isSplitBusy
        ? createPortal(
            <div className="fixed inset-0 z-[2147483647] flex items-center justify-center bg-black/75">
              <div className="flex items-center gap-3 rounded-lg bg-white px-4 py-3 shadow-lg">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-800" />
                <span className="text-sm font-medium text-gray-800">{SPLIT_MESSAGES[splitMsgIndex]}</span>
              </div>
            </div>,
            document.body
          )
        : null}
      {isSplitConfirmOpen && (
        <Modal isOpen={isSplitConfirmOpen} onOpenChange={onSplitConfirmChange}>
          <ModalContent>
            {(onClose) => (
              <>
                <ModalHeader>Absatz teilen</ModalHeader>
                <ModalBody>
                  <p>Möchten Sie den Absatz an dieser Stelle teilen?</p>
                </ModalBody>
                <ModalFooter>
                  <Button
                    variant="bordered"
                    color="danger"
                    radius="full"
                    onPress={() => {
                      setSplitConfirmIndex(null);
                      onClose();
                    }}
                  >
                    Abbrechen
                  </Button>
                  <AppButton
                    width="w-[110px]"
                    onClick={async () => {
                      const index = splitConfirmIndex;
                      setSplitConfirmIndex(null);
                      onClose();
                      if (index != null) {
                        await handleSplitExecute(index);
                      }
                    }}
                  >
                    Teilen
                  </AppButton>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </Modal>
      )}
      <div ref={nestedRefs.current[indexParagraphRef]} id={nestedKey} className="flex">
        {isModalOpen && (
          <EditParagraphMetaModal
            paragraph={nestedSectionData}
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            sessionId={id}
            speakers={speakers || []}
            onOpenSpeakersModal={onOpenSpeakersModal}
          />
        )}
        {isDeleteOpen && (
          <DeleteParagraphConfirmModal
            paragraphId={nestedSectionData.id}
            isOpen={isDeleteOpen}
            sessionId={id}
            onClose={() => setIsDeleteOpen(false)}
          />
        )}
        {isSplitMergeOpen && (
          <SplitMergeModal
            isOpen={isSplitMergeOpen}
            onClose={() => setIsSplitMergeOpen(false)}
            currentParagraph={nestedSectionData}
            previousParagraph={previousParagraph}
            nextParagraph={nextParagraph}
            speakers={speakers || []}
            onSave={handleSplitMergeSave}
          />
        )}
        {/* Left Column */}
        <div className="flex flex-col md:flex-row w-full gap-4">
          {/* Left Column – Raw Content */}
          <div className="flex flex-col p-2 ps-4 pt-2 w-full md:w-1/2">
            <span className="text-xs font-semibold uppercase text-gray-500 mb-3">ROHTRANSKRIPT</span>
            {syncDebugInfo && isCurrentParagraph && (
              <div className="mb-2 rounded border border-amber-300 bg-amber-50 p-2 font-mono text-[11px] leading-snug text-amber-900">
                <div className="font-semibold">SYNC DEBUG (paragraph {syncDebugInfo.paragraphId})</div>
                <div>rendered_words: {syncDebugInfo.rendered}  |  db_words: {syncDebugInfo.dbCount}  |  timeline: {syncDebugInfo.timelineLen}</div>
                <div>mode: {syncDebugInfo.mode}</div>
                <div>paragraph: [{Number(syncDebugInfo.paragraphStart).toFixed(2)}s, {syncDebugInfo.effectiveParagraphEnd != null ? Number(syncDebugInfo.effectiveParagraphEnd).toFixed(2) : "?"}s]</div>
                <div>audio_t: {Number.isFinite(syncDebugInfo.currentTime) ? syncDebugInfo.currentTime.toFixed(3) : "—"}s  |  active_word_index: {syncDebugInfo.activeWordIndex ?? "—"}  |  active_offset: {Number.isFinite(syncDebugInfo.activeOffset) ? syncDebugInfo.activeOffset.toFixed(3) : "—"}s</div>
                <div>shown_for: {Number.isFinite(syncDebugInfo.shownForSeconds) ? syncDebugInfo.shownForSeconds.toFixed(3) + "s" : "—"}  |  expected_duration: {Number.isFinite(syncDebugInfo.expectedWordDuration) ? syncDebugInfo.expectedWordDuration.toFixed(3) + "s" : "—"}</div>
                <div>delta (audio - active_offset): {Number.isFinite(syncDebugInfo.delta) ? syncDebugInfo.delta.toFixed(3) + "s" : "—"}  (positive = highlight lags; negative = highlight leads)</div>
              </div>
            )}
            <div
              ref={rawContentContainerRef}
              className="relative w-full rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-900 whitespace-pre-wrap break-words leading-relaxed"
              onPointerDown={isInteractive ? handleRawPointerDown : undefined}
              onPointerMove={isInteractive ? handleRawPointerMove : undefined}
              onPointerUp={isInteractive ? handleRawPointerUp : undefined}
              onPointerCancel={isInteractive ? handleRawPointerCancel : undefined}
              style={{
                userSelect: dragRange ? "none" : undefined,
                WebkitUserSelect: dragRange ? "none" : undefined,
              }}
            >
              {rawContent
                ? (highlightedRawContent || rawContent)
                : <span className="text-gray-400">Kein Rohtranskript vorhanden.</span>}
              {pendingHighlight ? (
                <div
                  role="dialog"
                  aria-label="Farbe für Hervorhebung wählen"
                  className="absolute z-30 flex items-center gap-1 rounded-full bg-white p-1.5 shadow-lg ring-1 ring-gray-200"
                  style={{
                    top: pendingHighlight.anchorPos.top,
                    left: pendingHighlight.anchorPos.left,
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onPointerUp={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {HIGHLIGHT_PALETTE.map((swatch) => (
                    <button
                      key={swatch.id}
                      type="button"
                      onClick={() => handleConfirmHighlight(swatch.color)}
                      disabled={isCreatingHighlight}
                      className="h-6 w-6 rounded-full ring-1 ring-gray-300 transition hover:scale-110 disabled:cursor-not-allowed disabled:opacity-60"
                      style={{ backgroundColor: swatch.color }}
                      aria-label={swatch.label}
                      title={swatch.label}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => setPendingHighlight(null)}
                    className="ml-1 flex h-6 w-6 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
                    aria-label="Abbrechen"
                    title="Abbrechen"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          {/* Right Column – Toggle between Original & Corrected */}
          <div className="flex flex-col p-2 pe-4 pt-0 w-full md:w-1/2 gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase text-gray-500">Arbeitsfeld</span>
              <div className="flex items-center gap-2">
                <div className="flex rounded-full border border-gray-200 bg-white p-0.5">
                  {[
                    { key: "original", label: "Hard" },
                    { key: "corrected", label: "Soft" },
                  ].map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => {
                        setActiveContentView(option.key);
                        // Update global mode if user manually changes local toggle
                        if (option.key === "original" && promptMode !== "hard") {
                          // This will be handled by the parent if needed
                        } else if (option.key === "corrected" && promptMode !== "soft") {
                          // This will be handled by the parent if needed
                        }
                      }}
                      className={classNames(
                        "px-3 py-1 text-xs font-medium rounded-full transition-colors",
                        option.key === activeContentView
                          ? "bg-secondaryContainer text-onSecondaryContainer"
                          : "text-gray-600 hover:text-gray-900"
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <Button
                  isIconOnly
                  onPress={handleRegeneration}
                  className="bg-background hover:bg-gray-200 rounded-full"
                  size="sm"
                >
                  <RotateCcw className="w-5 h-5 text-gray-700" />
                </Button>
                <Dropdown className="text-onSurface bg-surfaceContainer p-0 border-0">
                  <DropdownTrigger>
                    <Button isIconOnly className="h-8 w-8 hover:bg-gray-200 bg-surface rounded-full">
                      <MoreVertical className="w-5 h-5" />
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu
                    className="p-0"
                    aria-label="Project actions"
                    onAction={(key) => {
                      if (key === "edit") setIsModalOpen(true);
                      else if (key === "delete") {
                        setIsDeleteOpen(true)
                      }
                      else if (key === "filter") {
                        setSelectedSpeakers(new Set([nestedSectionData.speaker_name]))
                      }
                      else if (key === "copy") {
                        handleCopyActiveText();
                      }
                      else if (key === "split_merge") {
                        setIsSplitMergeOpen(true)
                      }
                    }}
                  > 
                  <DropdownItem
                    key="edit"
                    startContent={<SquarePen className="w-4 h-4 " />}
                    className="text-sm p-4 py-3 rounded-xl hover:bg-surfaceContainer"
                    textValue="Absatz bearbeiten"
                  >
                    Sprecher bearbeiten
                  </DropdownItem>
                  <DropdownItem
                    key="filter"
                    startContent={<Filter className="w-4 h-4 " />}
                    className="text-sm p-4 py-3 rounded-xl hover:bg-surfaceContainer"
                    textValue="Sprecher Filtern"
                  >
                    Sprecher filtern
                  </DropdownItem>
                 
                  <DropdownItem
                    key="copy"
                    startContent={<Copy className="w-4 h-4 " />}
                    className="text-sm p-4 py-3 rounded-xl hover:bg-surfaceContainer"
                    textValue="Text kopieren"
                  >
                    Text kopieren
                  </DropdownItem>
                  <DropdownItem
                    key="delete"
                    startContent={<Trash className="w-4 h-4 " />}
                    className="text-sm p-4 py-3 rounded-xl"
                    color="danger"
                    textValue="Absatz löschen"
                  >
                    Löschen
                  </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>
            </div>
            {isCorrectionPending ? (
              /* Split just happened — the AI columns are being rebuilt in
                 the background. Skeleton rather than the raw text, so the
                 user isn't misled into thinking this is the final wording. */
              <div className="w-full p-3" aria-busy="true" aria-live="polite">
                <div className="flex items-center gap-2 mb-3 text-xs text-onSurface/60">
                  <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Korrektur wird neu erstellt…
                </div>
                <div className="space-y-2 animate-pulse">
                  <div className="h-3 bg-gray-200 rounded w-full" />
                  <div className="h-3 bg-gray-200 rounded w-11/12" />
                  <div className="h-3 bg-gray-200 rounded w-4/5" />
                  <div className="h-3 bg-gray-200 rounded w-2/3" />
                </div>
              </div>
            ) : (
              <>
                {isCorrectionFailed && (
                  <div className="mx-3 mt-2 px-3 py-2 rounded-md bg-warning-50 text-xs text-warning-700 border border-warning-200">
                    Die automatische Korrektur konnte nicht erstellt werden.
                    Angezeigt wird der unkorrigierte Rohtext.
                  </div>
                )}
                <TextareaAutosize
                  value={activeContentView === "original" ? originalContentText : (correctedContentText ?? "").replace(/^\s/, '')}
                  onChange={(e) =>
                    activeContentView === "original"
                      ? setOriginalContentText(e.target.value)
                      : setCorrectedContentText(e.target.value)
                  }
                  className="w-full p-3 border-0 border-gray-300 rounded-md text-sm text-black resize-none"
                />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
};

export default React.memo(NestedAccordionItemContent);
